/**
 * ❌ Reject Corporate Account Modal
 * Reject pending corporate accounts with reason
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Grid,
  IconButton,
  Alert,
  MenuItem,
} from '@mui/material';
import {
  Close,
  Cancel,
  Business,
  Person,
  Email,
  Phone,
} from '@mui/icons-material';
import {
  CorporateAccount,
  corporateAccountsService,
} from '@/services/corporateAccountsService';
import toast from 'react-hot-toast';

interface RejectCorporateAccountModalProps {
  open: boolean;
  account: CorporateAccount | null;
  onClose: () => void;
  onReject: () => void;
}

const REJECTION_REASONS = [
  'Incomplete or incorrect documentation',
  'Failed credit check',
  'Company not verified',
  'Suspicious activity detected',
  'Does not meet eligibility criteria',
  'Duplicate account',
  'Other',
];

export const RejectCorporateAccountModal: React.FC<RejectCorporateAccountModalProps> = ({
  open,
  account,
  onClose,
  onReject,
}) => {
  const [rejectionReason, setRejectionReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ reason?: string }>({});

  if (!account) return null;

  const validateForm = () => {
    const newErrors: { reason?: string } = {};

    const finalReason = rejectionReason === 'Other' ? customReason : rejectionReason;
    if (!finalReason || finalReason.trim().length === 0) {
      newErrors.reason = 'Rejection reason is required';
    } else if (finalReason.trim().length < 10) {
      newErrors.reason = 'Please provide a more detailed reason (minimum 10 characters)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    const finalReason = rejectionReason === 'Other' ? customReason : rejectionReason;

    setSubmitting(true);
    try {
      const response = await corporateAccountsService.rejectCorporateAccount({
        accountId: account.id,
        reason: finalReason,
      });

      if (response.success) {
        toast.success(`${account.companyName} has been rejected`);
        onReject();
        handleClose();
      } else {
        toast.error(response.error || 'Failed to reject corporate account');
      }
    } catch (error) {
      console.error('Failed to reject account:', error);
      toast.error('Failed to reject corporate account');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setRejectionReason('');
    setCustomReason('');
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Cancel color="error" />
            <Typography variant="h6">Reject Corporate Account</Typography>
          </Box>
          <IconButton onClick={handleClose} disabled={submitting}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="warning" sx={{ mb: 3 }}>
          You are about to reject this corporate account. This action will notify the company contact
          person.
        </Alert>

        {/* Account Information */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Business color="primary" fontSize="small" />
            <Typography variant="subtitle2">{account.companyName}</Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Person fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {account.contactPerson.name}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Email fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {account.contactPerson.email}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Phone fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {account.contactPerson.phone}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Rejection Reason Selection */}
        <TextField
          fullWidth
          required
          select
          label="Rejection Reason"
          value={rejectionReason}
          onChange={(e) => {
            setRejectionReason(e.target.value);
            if (e.target.value !== 'Other') {
              setCustomReason('');
            }
          }}
          error={!!errors.reason && !rejectionReason}
          helperText={
            !!errors.reason && !rejectionReason
              ? errors.reason
              : 'Select the primary reason for rejection'
          }
          disabled={submitting}
          sx={{ mb: 2 }}
        >
          <MenuItem value="" disabled>
            <em>Select a reason</em>
          </MenuItem>
          {REJECTION_REASONS.map((reason) => (
            <MenuItem key={reason} value={reason}>
              {reason}
            </MenuItem>
          ))}
        </TextField>

        {/* Custom Reason Input (shown when "Other" is selected) */}
        {rejectionReason === 'Other' && (
          <TextField
            fullWidth
            required
            label="Custom Reason"
            multiline
            rows={3}
            value={customReason}
            onChange={(e) => setCustomReason(e.target.value)}
            error={!!errors.reason}
            helperText={errors.reason || 'Provide a detailed reason for rejection'}
            placeholder="Please specify the reason for rejection..."
            disabled={submitting}
            sx={{ mb: 2 }}
          />
        )}

        {/* Additional Details (shown for predefined reasons) */}
        {rejectionReason && rejectionReason !== 'Other' && (
          <TextField
            fullWidth
            label="Additional Details (Optional)"
            multiline
            rows={3}
            value={customReason}
            onChange={(e) => setCustomReason(e.target.value)}
            placeholder="Add any additional details..."
            disabled={submitting}
            helperText="Provide more context if needed"
          />
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleSubmit}
          disabled={submitting || !rejectionReason}
          startIcon={<Cancel />}
        >
          {submitting ? 'Rejecting...' : 'Reject Account'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
